'use client'

// The old map: the torn corner of it he can find.
//
// tornMap.glb, one mesh per section of lib/world/mapSheet.js, named
// map_<id>: the torn corner of the old town (0.56 by 0.40, drawn as the
// town is seen from above with north (+z) up, the north corner torn away
// through the railway and the road out past the flats), and the detailed
// sheet of the north line he asked for (granted 5 Oct, a whole sheet in
// the same pencil hand, the line running north to an old station). A piece
// found at the junk heap lies there until he finds it; found pieces lie in
// front of his hoard, at the nook's map_spot and map_spot_2 (hoard.glb).

import { useEffect, useMemo, useState } from 'react'
import * as THREE from 'three/webgpu'
import { ditherMaterial } from '../effects/ditherRegistry'
import { useModel } from '../hooks/useModel'
import { toNode } from '../hooks/useSceneNodeMaterials'

// The dumpsters have no POI of their own: the sim places activity_junkheap
// at this spot (sim-server/src/world/Entities.js). Mirrored here, a step
// to the side so the paper is not inside the bin.
const JUNK = [52.6, 0, -12.9]
const POLL_MS = 60_000
const MODEL = '/models/tornMap.glb'
const NOOK = '/models/hoard.glb'

export function TornMap() {
  const [pieces, setPieces] = useState([])
  useEffect(() => {
    let alive = true
    const pull = async () => {
      try {
        const res = await fetch('/api/map', { cache: 'no-store' })
        const j = await res.json()
        if (alive && j?.enabled) setPieces(j.pieces || [])
      } catch { /* the paper stays where it was */ }
    }
    pull()
    const t = setInterval(pull, POLL_MS)
    return () => { alive = false; clearInterval(t) }
  }, [])

  const { scene } = useModel(MODEL)
  const { scene: nook } = useModel(NOOK)
  // Each sheet's mesh by its section id (map_<id>), its material made a
  // node material and dithered at birth
  const sheets = useMemo(() => {
    const out = new Map()
    const cache = new Map()
    scene?.traverse((o) => {
      const m = /^map_([a-z_]+)$/.exec(o.name || '')
      if (!m || !o.isMesh) return
      const material = toNode(o.material, cache, 1)
      ditherMaterial(material)
      out.set(m[1], { geometry: o.geometry, material })
    })
    return out
  }, [scene])
  // Where the found sheets lie: in front of the hoard, as the nook says,
  // the first at map_spot and the next at map_spot_2
  const kept = useMemo(() => {
    const out = []
    if (!nook) return out
    nook.updateMatrixWorld(true)
    for (const name of ['map_spot', 'map_spot_2']) {
      let spot = null
      nook.traverse((o) => { if (!spot && o.name === name) spot = o })
      if (!spot) continue
      const p = new THREE.Vector3(); const q = new THREE.Quaternion(); const s = new THREE.Vector3()
      spot.matrixWorld.decompose(p, q, s)
      out.push({ position: [p.x, p.y, p.z], quaternion: q })
    }
    return out
  }, [nook])

  // Found sheets in the order he found them; a piece nobody has found lies
  // where it is found only if that is the junk heap (a granted map is his
  // the moment it is granted, and never lies about)
  const found = pieces.filter((p) => p.open && p.found).sort((a, b) => (a.at || 0) - (b.at || 0))
  return (
    <group name="torn_map">
      {found.map((p, i) => {
        const sheet = sheets.get(p.id); const at = kept[i]
        if (!sheet || !at) return null
        return <mesh key={p.id} name={`map_${p.id}`} geometry={sheet.geometry} material={sheet.material} position={at.position} quaternion={at.quaternion} receiveShadow />
      })}
      {pieces.filter((p) => p.open && !p.found && p.foundAt === 'scavenge').map((p) => {
        const sheet = sheets.get(p.id)
        if (!sheet) return null
        return <mesh key={p.id} name={`map_${p.id}`} geometry={sheet.geometry} material={sheet.material} position={JUNK} rotation={[0, -0.7, 0]} receiveShadow />
      })}
    </group>
  )
}

export default TornMap
